import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
    Collection__CreatePayload,
    Collection__DeletePayload,
    Collection__SearchPayload,
    Collection__UpdatePayload,
    Collection_ContentSearchPayload,
    Collection_DeleteMetadata,
    CreateCollectionDocument,
    CreateCollectionMutation,
    CreateCollectionMutationVariables,
    DeleteCollectionDocument,
    DeleteCollectionMutation,
    DeleteCollectionMutationVariables,
    DeleteMetadata_Payload,
    DeleteMetdataDocument,
    DeleteMetdataMutation,
    DeleteMetdataMutationVariables,
    UpdateCollectionDocument,
    UpdateCollectionMutation,
    UpdateCollectionMutationVariables,
    useSearchCollectionsLazyQuery as _useSearchCollectionsLazyQuery,
    useSearchContentCollectionsLazyQuery as _useSearchContentCollectionsLazyQuery,
} from '../generated/graphql-types';

export const useCreateCollectionMutation = () => {
    const [_createCollection, { data: result, error, loading }] = useMutation<
        CreateCollectionMutation,
        CreateCollectionMutationVariables
    >(CreateCollectionDocument);

    return {
        data: result?.Collection_Create,
        error,
        loading,
        createCollection: (data: Collection__CreatePayload) => {
            return _createCollection({
                variables: {
                    data,
                },
            });
        },
    };
};

export const useUpdateCollectionMutation = () => {
    const [_updatecollection, { data: result, error, loading }] = useMutation<
        UpdateCollectionMutation,
        UpdateCollectionMutationVariables
    >(UpdateCollectionDocument);

    return {
        data: result?.Collection_Update,
        error,
        loading,
        updateCollection: (data: Collection__UpdatePayload) => {
            return _updatecollection({
                variables: {
                    data,
                },
            });
        },
    };
};

export const useDeleteCollectionMutation = () => {
    const [_deletecollection, { data: result, error, loading }] = useMutation<
        DeleteCollectionMutation,
        DeleteCollectionMutationVariables
    >(DeleteCollectionDocument);

    return {
        data: result?.Collection_Delete,
        error,
        loading,
        deleteCollection: (data: Collection__DeletePayload) => {
            return _deletecollection({
                variables: {
                    data,
                },
            });
        },
    };
};

/**
 * Metadata type mapping for deletion statistics
 * Maps API metadata types to display categories
 */
enum MetadataType {
    FOLDER = 'FOLDER', // Collections
    LIVEBOARD = 'LIVEBOARD',
    ANSWER = 'ANSWER',
    WORKSHEET = 'WORKSHEET',
    MODEL = 'MODEL',
    TABLE = 'TABLE',
    VIEW = 'VIEW',
    DATASET = 'DATASET',
    LOGICAL_TABLE = 'LOGICAL_TABLE',
    ONE_TO_ONE_LOGICAL = 'ONE_TO_ONE_LOGICAL',
}

/**
 * Categories for deletion statistics display
 */
export interface DeletionStats {
    collections: number;
    liveboards: number;
    answers: number;
    models: number; // WORKSHEET + MODEL (combined)
    tables: number; // TABLE + LOGICAL_TABLE + ONE_TO_ONE_LOGICAL
    views: number; // VIEW
    datasets: number; // DATASET
    metadataDeleted?: Collection_DeleteMetadata[];
}

/**
 * Parse metadata from API response and categorize into display categories
 *
 * Categorization rules:
 * - FOLDER → collections
 * - LIVEBOARD → liveboards
 * - ANSWER → answers
 * - WORKSHEET + MODEL → worksheets (combined as one entity)
 * - TABLE + LOGICAL_TABLE + ONE_TO_ONE_LOGICAL → tables
 * - VIEW → views
 * - DATASET → datasets
 *
 * @param metadataDeleted Array of metadata objects from API response
 * @returns Categorized counts
 */
const parseMetadataStats = (
    metadataDeleted: Collection_DeleteMetadata[],
): DeletionStats => {
    const stats: DeletionStats = {
        collections: 0,
        liveboards: 0,
        answers: 0,
        models: 0,
        tables: 0,
        views: 0,
        datasets: 0,
        metadataDeleted,
    };

    metadataDeleted.forEach(item => {
        const count = item.identifiers?.length || 0;
        const type = (item.type || '').toUpperCase();

        switch (type) {
            case MetadataType.FOLDER:
                stats.collections += count;
                break;

            case MetadataType.LIVEBOARD:
                stats.liveboards += count;
                break;

            case MetadataType.ANSWER:
                stats.answers += count;
                break;

            // Worksheet and Model are counted as one entity
            case MetadataType.WORKSHEET:
            case MetadataType.MODEL:
                stats.models += count;
                break;

            // Table types
            case MetadataType.TABLE:
            case MetadataType.LOGICAL_TABLE:
            case MetadataType.ONE_TO_ONE_LOGICAL:
                stats.tables += count;
                break;

            case MetadataType.VIEW:
                stats.views += count;
                break;

            case MetadataType.DATASET:
                stats.datasets += count;
                break;

            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown metadata type: ${type}`);
        }
    });

    return stats;
};

/**
 * Hook to fetch statistics about what will be deleted when deleting collections
 * Uses the delete API with dry_run: true to get deletion preview without actually deleting
 *
 * @returns Object with fetchDeletionStats method
 */
export const useDeleteCollectionStats = () => {
    const [_deletecollection] = useMutation<
        DeleteCollectionMutation,
        DeleteCollectionMutationVariables
    >(DeleteCollectionDocument);

    const fetchDeletionStats = useCallback(
        async (collectionIds: string[]): Promise<DeletionStats> => {
            try {
                // Call delete API with dry_run: true to get preview
                const deletePayload: Collection__DeletePayload = {
                    folder_identifiers: collectionIds,
                    delete_children: true,
                    dry_run: true,
                };

                const response = await _deletecollection({
                    variables: {
                        data: deletePayload,
                    },
                });

                const data = response.data?.Collection_Delete;

                if (
                    !data ||
                    (data.Status && (data.Status < 200 || data.Status >= 300))
                ) {
                    throw new Error(
                        data?.Error || 'Failed to fetch deletion stats',
                    );
                }

                // Parse metadataDeleted array from response
                const metadataDeleted = (data?.Data?.metadataDeleted ??
                    []) as Collection_DeleteMetadata[];

                // Parse and categorize metadata
                const stats = parseMetadataStats(metadataDeleted);

                return stats;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error fetching deletion stats:', error);
                throw error;
            }
        },
        [_deletecollection],
    );

    return {
        fetchDeletionStats,
    };
};

export const useDeleteMetdataMutation = () => {
    const [_deleteMetadata, { data: result, error, loading }] = useMutation<
        DeleteMetdataMutation,
        DeleteMetdataMutationVariables
    >(DeleteMetdataDocument);

    return {
        data: result?.MetadataCollection_Delete,
        error,
        loading,
        deleteMetadata: (data: DeleteMetadata_Payload) => {
            return _deleteMetadata({
                variables: {
                    data,
                },
            });
        },
    };
};
export const useSearchCollectionsLazyQuery = () => {
    const [
        _searchCollections,
        { data, error, loading },
    ] = _useSearchCollectionsLazyQuery({
        fetchPolicy: 'network-only',
    });

    return {
        searchCollections: (data: Collection__SearchPayload) => {
            return _searchCollections({
                variables: {
                    data,
                },
            });
        },
        status: data?.Collection_Search?.Status,
        data: data?.Collection_Search?.Data,
        error,
        loading,
    };
};

export const useSearchContentCollectionsLazyQuery = () => {
    const [
        _searchContentCollections,
        { data, error, loading },
    ] = _useSearchContentCollectionsLazyQuery({
        fetchPolicy: 'network-only',
    });

    return {
        searchContentCollections: (data: Collection_ContentSearchPayload) => {
            return _searchContentCollections({
                variables: {
                    data,
                },
            });
        },
        data: data?.Collection_ContentSearch?.Data,
        status: data?.Collection_ContentSearch?.Status,
        error,
        loading,
    };
};
